import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  ScatterChart, Scatter, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  ReferenceLine, Cell,
} from 'recharts';
import {
  RefreshCw, TrendingUp, TrendingDown, Factory, Zap, DollarSign, Leaf,
  Target, BarChart3, Activity, AlertCircle,
} from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';

/**
 * Executive Dashboard panel for OIM (M1).
 *
 * Surfaces management-level KPIs + trends pulled from existing OIM tables
 * (energy / production / cost / intensity) plus optional carbon emissions
 * cross-linked from MRV's mrv_emission_calc.
 */

const PERIOD_OPTIONS = [
  { id: '7d',   label: 'Last 7 days' },
  { id: '30d',  label: 'Last 30 days' },
  { id: '90d',  label: 'Last 90 days' },
  { id: '180d', label: 'Last 180 days' },
  { id: '365d', label: 'Last 365 days' },
];

const ENERGY_TYPE_COLOR = {
  electricity: '#2563eb',
  steam:       '#f59e0b',
  bagasse:     '#16a34a',
  rice_husk:   '#84cc16',
  husk:        '#84cc16',
  biomass:     '#22c55e',
  spent_wash:  '#a855f7',
  coal:        '#475569',
  diesel:      '#dc2626',
  petrol:      '#ec4899',
  natural_gas: '#0891b2',
  gas:         '#0891b2',
  lpg:         '#0ea5e9',
  furnace_oil: '#9a3412',
  fuel:        '#7c2d12',
  wood:        '#84cc16',
  renewable:   '#10b981',
};

const fmtNumber = (n, d = 0) => {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
};

const fmtCompact = (n) => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(2) + 'K';
  return v.toFixed(0);
};

export default function OimExecutivePanel({ siteId }) {
  const { showError } = useAlert();
  const [period, setPeriod] = useState('90d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ site_id: siteId, period });
      const res = await axios.get(`${API}/oim/exec-dashboard?${params}`);
      setData(res.data);
    } catch (e) {
      console.error('OIM exec-dashboard fetch failed', e);
      showError(e.response?.data?.detail || 'Failed to load executive dashboard');
    } finally {
      setLoading(false);
    }
  }, [siteId, period, showError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!siteId) {
    return (
      <div className="p-12 text-center text-gray-500">
        <AlertCircle className="w-10 h-10 mx-auto text-gray-300 mb-2" />
        Select a site from the top selector to view the executive dashboard.
      </div>
    );
  }

  const k = data?.kpis || {};
  const currency = k.currency || 'INR';
  const trendDelta = k.savings_pct;
  const trendIsGood = trendDelta != null && trendDelta > 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Executive Dashboard</h2>
          <div className="text-xs text-gray-500">Operational, sustainability and financial KPIs</div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-2 py-1.5 border rounded text-sm"
          >
            {PERIOD_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <button
            type="button" onClick={fetchData} disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* KPI cards (top row of 4 + secondary row of 4) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard
          label="Production"
          value={`${fmtCompact(k.production_total)} ${k.production_unit || ''}`.trim()}
          sub={`Total over ${data?.days || '—'} days`}
          Icon={Factory}
          accent="emerald"
          loading={loading}
        />
        <KpiCard
          label="Total Energy"
          value={`${fmtCompact(k.total_energy_kwh)} kWh`}
          sub="kWh-equivalent across all fuels"
          Icon={Zap}
          accent="blue"
          loading={loading}
        />
        <KpiCard
          label="Energy Cost"
          value={`${currency} ${fmtCompact(k.energy_cost)}`}
          sub={`${currency} ${fmtNumber(k.cost_per_unit, 2)} per ${k.production_unit || 'unit'}`}
          Icon={DollarSign}
          accent="amber"
          loading={loading}
        />
        <KpiCard
          label="Carbon Emissions"
          value={k.carbon_tco2e != null ? `${fmtNumber(k.carbon_tco2e, 2)} tCO2e` : '—'}
          sub={k.carbon_tco2e == null ? 'MRV not enabled or no calc run' : 'From MRV emission calc'}
          Icon={Leaf}
          accent="violet"
          loading={loading}
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard
          label="Energy Intensity (actual)"
          value={k.actual_intensity != null ? `${fmtNumber(k.actual_intensity, 2)} kWh/${k.production_unit || ''}` : '—'}
          sub="Current period average"
          Icon={Activity}
          accent="blue"
          loading={loading}
        />
        <KpiCard
          label="Baseline Intensity"
          value={k.baseline_intensity != null ? `${fmtNumber(k.baseline_intensity, 2)} kWh/${k.production_unit || ''}` : '—'}
          sub="Previous period average"
          Icon={Target}
          accent="gray"
          loading={loading}
        />
        <KpiCard
          label="Savings Achieved"
          value={trendDelta != null ? `${trendDelta > 0 ? '↓' : '↑'} ${Math.abs(trendDelta).toFixed(1)}%` : '—'}
          sub={trendDelta == null ? 'Need 2 periods of data' : (trendIsGood ? 'Lower intensity vs baseline' : 'Higher intensity vs baseline')}
          Icon={trendIsGood ? TrendingDown : TrendingUp}
          accent={trendIsGood ? 'emerald' : 'red'}
          loading={loading}
        />
        <KpiCard
          label="Energy mix"
          value={`${data?.energy_pareto?.length || 0} fuels`}
          sub={data?.energy_pareto?.[0] ? `Top: ${data.energy_pareto[0].energy_type} (${data.energy_pareto[0].share_pct}%)` : '—'}
          Icon={BarChart3}
          accent="violet"
          loading={loading}
        />
      </div>

      {/* Charts row 1: Monthly Energy Trend + Top Fuel Pareto */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Monthly Energy Trend</h3>
          <p className="text-xs text-gray-500 mb-2">Total energy consumption (kWh-equivalent), all fuel types</p>
          {(data?.monthly_energy_trend || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.monthly_energy_trend}>
                <defs>
                  <linearGradient id="energyGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.5}/>
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
                <XAxis dataKey="month" tick={{ fontSize: 11 }}
                       tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', year: '2-digit' }) : ''} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                <Tooltip
                  formatter={(v) => `${fmtNumber(v, 0)} kWh`}
                  labelFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : ''}
                />
                <Area type="monotone" dataKey="total_kwh" stroke="#2563eb"
                      fill="url(#energyGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <Empty hint="No energy data yet for this period" />
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Top Energy Sources (Pareto)</h3>
          <p className="text-xs text-gray-500 mb-2">By kWh-equivalent share</p>
          {(data?.energy_pareto || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.energy_pareto.slice(0, 8)} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false}/>
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmtCompact} />
                <YAxis type="category" dataKey="energy_type" tick={{ fontSize: 11 }} width={88} interval={0}/>
                <Tooltip formatter={(v, _, p) => [`${fmtNumber(v, 0)} kWh (${p.payload.share_pct}%)`, p.payload.energy_type]} />
                <Bar dataKey="kwh_eq" radius={[0, 4, 4, 0]}>
                  {data.energy_pareto.slice(0, 8).map((row, i) => (
                    <Cell key={i} fill={ENERGY_TYPE_COLOR[row.energy_type] || '#64748b'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty hint="No fuel data yet" />
          )}
        </div>
      </div>

      {/* Charts row 2: Production vs Energy correlation + Cost trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Production vs Energy Consumption</h3>
          <p className="text-xs text-gray-500 mb-2">Daily — each dot is one day. Tighter cluster = more consistent process.</p>
          {(data?.production_vs_energy || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                <XAxis type="number" dataKey="production" name="Production" unit={` ${k.production_unit || ''}`} tick={{ fontSize: 10 }} tickFormatter={fmtCompact}/>
                <YAxis type="number" dataKey="energy_kwh" name="Energy" unit=" kWh" tick={{ fontSize: 10 }} tickFormatter={fmtCompact}/>
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  formatter={(v, n) => [fmtNumber(v, 0), n]}
                  labelFormatter={() => ''}
                />
                <Scatter data={data.production_vs_energy} fill="#059669" />
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <Empty hint="Not enough paired data — upload both energy + production CSVs" />
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Cost Trend</h3>
          <p className="text-xs text-gray-500 mb-2">Daily total cost ({currency})</p>
          {(data?.cost_trend || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.cost_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
                <XAxis dataKey="date" tick={{ fontSize: 10 }}
                       tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}/>
                <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtCompact}/>
                <Tooltip
                  formatter={(v) => `${currency} ${fmtNumber(v, 0)}`}
                  labelFormatter={v => v || ''}
                />
                <Line type="monotone" dataKey="total_cost" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }}/>
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Empty hint="No cost data yet — upload cost CSV" />
          )}
        </div>
      </div>

      {/* Charts row 3: Carbon trend + Site comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Carbon Emissions Trend</h3>
          <p className="text-xs text-gray-500 mb-2">Monthly tCO2e from MRV calc engine</p>
          {(data?.carbon_trend || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.carbon_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
                <XAxis dataKey="month" tick={{ fontSize: 11 }}
                       tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', year: '2-digit' }) : ''}/>
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${fmtNumber(v, 2)} tCO2e`} />
                <Line type="monotone" dataKey="tco2e" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }}/>
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Empty hint="No emissions calc yet — run MRV → Calculate emissions" />
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Site Comparison</h3>
          <p className="text-xs text-gray-500 mb-2">Total kWh-equivalent across sites you have access to</p>
          {(data?.sites_comparison || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(160, (data.sites_comparison.length || 1) * 32)}>
              <BarChart data={data.sites_comparison} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false}/>
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmtCompact}/>
                <YAxis type="category" dataKey="site_name" width={140} tick={{ fontSize: 11 }} interval={0}/>
                <Tooltip formatter={(v) => `${fmtNumber(v, 0)} kWh`} />
                <Bar dataKey="total_kwh" fill="#0ea5e9" radius={[0, 4, 4, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty hint="No site-level energy data in this period" />
          )}
        </div>
      </div>

      {/* Baseline vs actual — only render when there's something meaningful to
          compare. When baseline is 0 or missing, the previous period has no
          intensity data (compute hasn't run there, or the site is new) — show
          a clear "pending" message instead of a broken chart where Actual
          looks infinitely worse than zero. */}
      {(() => {
        const baseline = Number(k.baseline_intensity) || 0;
        const actual = Number(k.actual_intensity) || 0;
        const hasBaseline = baseline > 0;
        const hasActual = actual > 0;
        if (!hasBaseline && !hasActual) return null;

        const unit = k.production_unit || 'unit';
        const xMax = Math.max(baseline, actual) * 1.15 || 1;
        // Δ% — positive means Actual is higher than Baseline (regression);
        // negative means Actual is lower than Baseline (improvement).
        const deltaPct = hasBaseline ? ((actual - baseline) / baseline) * 100 : null;
        const better = deltaPct != null && deltaPct < 0;

        return (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Baseline vs Actual Energy Intensity</h3>
              {deltaPct != null && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                  better ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                }`}>
                  {deltaPct > 0 ? '+' : ''}{fmtNumber(deltaPct, 1)}% vs baseline
                  {' · '}{better ? 'improving' : 'regressing'}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Baseline = average intensity across the previous {data?.days || '—'} days; Actual = current period.
              Lower is better (less energy per unit of production).
            </p>

            {!hasBaseline ? (
              <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2 text-xs text-amber-800">
                No baseline yet — the previous {data?.days || '—'}-day window has no computed intensity rows
                to average against. Run compute over a longer history (or wait until the next scheduler tick)
                for a meaningful comparison.
              </div>
            ) : null}

            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={[
                { name: 'Baseline', value: baseline },
                { name: 'Actual',   value: actual },
              ]} layout="vertical" margin={{ left: 12, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false}/>
                <XAxis type="number" domain={[0, xMax]} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12, fontWeight: 600 }}/>
                <Tooltip formatter={(v) => `${fmtNumber(v, 2)} kWh/${unit}`} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  <Cell fill="#9ca3af"/>
                  {/* Actual is coloured by direction vs baseline. With no
                      baseline, use a neutral blue so we don't imply
                      regression against zero. */}
                  <Cell fill={
                    !hasBaseline ? '#3b82f6'
                      : better     ? '#10b981'
                      :              '#dc2626'
                  }/>
                </Bar>
                {hasBaseline && (
                  <ReferenceLine
                    x={baseline}
                    stroke="#6b7280"
                    strokeDasharray="3 3"
                    label={{ value: 'Baseline', position: 'top', fontSize: 10, fill: '#6b7280' }}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      })()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, Icon, accent = 'gray', loading }) {
  const accents = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    iconBg: 'bg-blue-100',    iconText: 'text-blue-600' },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   iconBg: 'bg-amber-100',   iconText: 'text-amber-600' },
    violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  iconBg: 'bg-violet-100',  iconText: 'text-violet-600' },
    red:     { bg: 'bg-red-50',     text: 'text-red-700',     iconBg: 'bg-red-100',     iconText: 'text-red-600' },
    gray:    { bg: 'bg-gray-50',    text: 'text-gray-700',    iconBg: 'bg-gray-100',    iconText: 'text-gray-600' },
  };
  const a = accents[accent] || accents.gray;
  if (loading) {
    return (
      <div className={`p-3 rounded-lg border border-gray-200 ${a.bg} animate-pulse`}>
        <div className="h-3 w-20 bg-gray-200 rounded mb-2"/>
        <div className="h-6 w-28 bg-gray-200 rounded mb-1"/>
        <div className="h-3 w-32 bg-gray-200 rounded"/>
      </div>
    );
  }
  return (
    <div className={`p-3 rounded-lg border border-gray-200 ${a.bg}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] font-semibold uppercase tracking-wide ${a.text}`}>{label}</div>
          <div className="text-xl font-bold text-gray-900 mt-1 truncate">{value}</div>
          {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
        </div>
        {Icon && (
          <div className={`w-8 h-8 rounded-md ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4 h-4 ${a.iconText}`} />
          </div>
        )}
      </div>
    </div>
  );
}

function Empty({ hint }) {
  return (
    <div className="flex flex-col items-center justify-center text-center text-gray-400 py-12 text-xs">
      <BarChart3 className="w-8 h-8 text-gray-300 mb-2"/>
      <div>{hint}</div>
    </div>
  );
}
